import { lazy } from 'react'
import { createBrowserRouter, Outlet } from 'react-router'

import { AuthGuard, isUserAllowed, RoleGuard } from '@shared/providers/src/MeProvider'
import { UserRole } from '@shared/utils'

import AuthenticatedProviders from '@providers/AuthenticatedProviders'
import Error from '@pages/Misc/Error'
import Forbidden from '@pages/Misc/Forbidden'
import NotAuthorized from '@pages/Misc/NotAuthorized'
import NotFound from '@pages/Misc/NotFound'
import AccountTab from '@pages/MyProfile/AccountTab'
import ProfileTab from '@pages/Patients/Patient/ProfileTab'

import Root from './Root'

const Environment = lazy(() => import('@pages/Environment'))
const MessagePreview = lazy(() => import('@pages/MessagePreview'))
const AuthRedirect = lazy(() => import('@pages/AuthRedirect'))
const Home = lazy(() => import('@pages/Home'))
const Scorecard = lazy(() => import('@pages/Dashboard/Scorecard'))
const Escalations = lazy(() => import('@pages/Escalations'))
const Kanban = lazy(() => import('@pages/Admin/Kanban'))
const Conversations = lazy(() => import('@pages/Conversations'))
const Conversation = lazy(() => import('@pages/Conversations/Conversation'))
const ConversationEmpty = lazy(() => import('@pages/Conversations/Conversation/Empty'))
const ConversationCompose = lazy(() => import('@pages/Conversations/Conversation/Compose'))
const MyProfile = lazy(() => import('@pages/MyProfile'))
const SettingsTab = lazy(() => import('@pages/MyProfile/SettingsTab'))
const SecurityTab = lazy(() => import('@pages/MyProfile/SecurityTab'))
const LicensesTab = lazy(() => import('@pages/MyProfile/LicensesTab'))
const Encounter = lazy(() => import('@pages/Encounters/Encounter'))
const EncounterFormer = lazy(() => import('@pages/Encounters/Encounter/EncounterFormer'))
const Appointments = lazy(() => import('@pages/Appointments'))
const HealthHistoryAlerts = lazy(() => import('@pages/HealthHistoryAlerts'))
const Labs = lazy(() => import('@pages/Labs'))
const Lab = lazy(() => import('@pages/Labs/Lab'))
const Prescriptions = lazy(() => import('@pages/Prescriptions'))
const Prescription = lazy(() => import('@pages/Prescriptions/Prescription'))
const Assessments = lazy(() => import('@pages/Assessments'))
const LabRequests = lazy(() => import('@pages/LabRequests'))
const Patients = lazy(() => import('@pages/Patients'))
const Patient = lazy(() => import('@pages/Patients/Patient'))
const DetailsTab = lazy(() => import('@pages/Patients/Patient/DetailsTab'))
const HealthHistoryTab = lazy(() => import('@pages/Patients/Patient/HealthHistoryTab'))
const ConversationsTab = lazy(() => import('@pages/Patients/Patient/ConversationsTab'))
const DocumentsTab = lazy(() => import('@pages/Patients/Patient/DocumentsTab'))
const AppointmentsTab = lazy(() => import('@pages/Patients/Patient/AppointmentsTab'))
const ActivityTab = lazy(() => import('@pages/Patients/Patient/ActivityTab'))
const PaymentsTab = lazy(() => import('@pages/Patients/Patient/PaymentsTab'))
const QuarterlyAutomationsTab = lazy(() => import('@pages/Patients/Patient/QuarterlyAutomationsTab'))
const ChatTab = lazy(() => import('@pages/Patients/Patient/ChatTab'))
const NotificationLogs = lazy(() => import('@pages/NotificationLogs'))
const ReportLogs = lazy(() => import('@pages/ReportLogs'))

const PatientEncounterRecommendVaccine = lazy(() => import('@pages/Encounters/Encounter/RecommendVaccine'))
const PatientEncounterScheduleAppointment = lazy(() => import('@pages/Encounters/Encounter/ScheduleAppointment/ScheduleAppointment'))
const PatientEncounterCreateReferral = lazy(() => import('@pages/Encounters/Encounter/CreateReferral'))
const PatientEncounterOrderLabs = lazy(() => import('@pages/Encounters/Encounter/OrderLabs'))
const PatientEncounterOrderRx = lazy(() => import('@pages/Encounters/Encounter/OrderRx'))
const PatientCreate = lazy(() => import('@pages/Patients/Create'))
const PlatformSettings = lazy(() => import('@pages/Admin/PlatformSettings/PlatformSettings'))
const Scheduling = lazy(() => import('@pages/Scheduling'))
const CBOs = lazy(() => import('@pages/CBOs/CBOs'))
const CBOCreate = lazy(() => import('@pages/CBOs/Create'))
const CBO = lazy(() => import('@pages/CBOs/CBO'))
const CBORuleSetDetails = lazy(() => import('@pages/CBOs/CBORuleSet/CBORuleSetDetails/CBORuleSetDetailsPage'))
const CBORuleSetEdit = lazy(() => import('@pages/CBOs/CBORuleSet/CBORuleSetEdit/CBORuleSetEditPage'))
const Pharmacies = lazy(() => import('@pages/Pharmacies'))
const DohFaxNumbers = lazy(() => import('@pages/DohFaxNumbers'))
const Reports = lazy(() => import('@pages/Reports'))
const Users = lazy(() => import('@pages/Users'))
const User = lazy(() => import('@pages/Users/User'))
const UserUpdate = lazy(() => import('@pages/Users/UpdateUser'))

const Login = lazy(() => import('@pages/Auth/Login'))
const Logout = lazy(() => import('@pages/Auth/Logout'))
const LoginAs = lazy(() => import('@pages/Auth/LoginAs'))
const CreatePassword = lazy(() => import('@pages/Auth/CreatePassword'))
const ForgotPassword = lazy(() => import('@pages/Auth/ForgotPassword'))
const UpdatePassword = lazy(() => import('@pages/Auth/UpdatePassword'))
const VisitV2 = lazy(() => import('@pages/VisitV2'))
const StandaloneAppointmentCall = lazy(() => import('@pages/Standalone/AppointmentCall'))
const StandalonePatientCall = lazy(() => import('@pages/Standalone/PatientCall'))
const StandaloneUserCall = lazy(() => import('@pages/Standalone/UserCall'))
const Redirect = lazy(() => import('@pages/Redirect'))
const Redirects = lazy(() => import('@pages/Redirects'))
const NotificationRedirect = lazy(() => import('@pages/NotificationRedirect'))

const LicensesReport = lazy(() => import('@pages/LicensesReport'))
const EnrolledPrescriptionsReport = lazy(() => import('@pages/Reports/pages/EnrolledPrescriptions'))
const EnrolledActivePatientsReport = lazy(() => import('@pages/Reports/pages/EnrolledActivePatients'))
const DaysToCompleteReport = lazy(() => import('@pages/Reports/pages/DaysToComplete'))
const WeeklyKPIsReport = lazy(() => import('@pages/Reports/pages/WeeklyKPIs'))
const AppointmentsMissedAndRescheduledReport = lazy(() => import('@pages/Reports/pages/AppointmentsMissedAndRescheduled'))
const EnrollmentsAndServiceLinesScheduledReport = lazy(() => import('@pages/Reports/pages/EnrollmentsAndServiceLinesScheduled'))
const InitialsCompletedReport = lazy(() => import('@pages/Reports/pages/InitialsCompleted'))
const PrepInitialScheduledFromDailyEnrollmentReport = lazy(() => import('@pages/Reports/pages/PrepInitialScheduledFromDailyEnrollment'))
const NewVsTransferPrepPatientsReport = lazy(() => import('@pages/Reports/pages/NewVsTransferPrepPatients'))
const NoShowPercentReport = lazy(() => import('@pages/Reports/pages/NoShowPercent'))
const PatientJourneysReport = lazy(() => import('@pages/Reports/pages/PatientJourneys'))

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      {
        path: '',
        element: <AuthRedirect />,
      },
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/logout',
        element: <Logout />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: '/update-password',
        element: <UpdatePassword />,
      },
      {
        path: '/create-password',
        element: <CreatePassword />,
      },
      {
        path: '/app',
        element: (
          <AuthGuard>
            <AuthenticatedProviders />
          </AuthGuard>
        ),
        children: [
          {
            path: '',
            element: <Home />,
          },
          {
            path: 'scorecard',
            element: (
              <RoleGuard redirect allowed={[UserRole.Provider, UserRole.Support, UserRole.MA]}>
                <Scorecard />
              </RoleGuard>
            ),
          },
          {
            path: 'kanban',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <Kanban />
              </RoleGuard>
            ),
          },
          {
            path: 'escalations',
            element: (
              <RoleGuard redirect allowed={[UserRole.Provider, UserRole.CBO, UserRole.MA, UserRole.Support]}>
                <Escalations />
              </RoleGuard>
            ),
          },
          {
            path: 'profile',
            children: [
              {
                path: '',
                element: <MyProfile />,
                children: [
                  {
                    path: '',
                    element: <AccountTab />,
                  },
                  {
                    path: 'security',
                    element: <SecurityTab />,
                  },
                  {
                    path: 'settings',
                    element: <SettingsTab />,
                  },
                  {
                    path: 'licenses',
                    element: (
                      <RoleGuard redirect allowed={[UserRole.Provider]}>
                        <LicensesTab />
                      </RoleGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'conversations',
            element: (
              <RoleGuard redirect prohibited={[UserRole.Compliance]}>
                <Conversations />
              </RoleGuard>
            ),
            children: [
              {
                path: '',
                element: <ConversationEmpty />,
              },
              {
                path: 'compose',
                element: (
                  <RoleGuard redirect prohibited={[UserRole.ClinicalLHC, UserRole.ClinicalQI]}>
                    <ConversationCompose />
                  </RoleGuard>
                ),
              },
              {
                path: ':id',
                element: <Conversation />,
              },
            ],
          },
          {
            path: 'appointments',
            element: (
              <RoleGuard redirect prohibited={[UserRole.Pharmacy, UserRole.PharmacyManager]}>
                <Appointments />
              </RoleGuard>
            ),
          },
          {
            path: 'hh-alerts',
            element: (
              <RoleGuard redirect predicate={(me) => me.superAdmin || isUserAllowed(me, [UserRole.Provider])}>
                <HealthHistoryAlerts />
              </RoleGuard>
            ),
          },
          {
            path: 'labs',
            children: [
              {
                path: '',
                element: <Labs />,
              },
              {
                path: ':id',
                element: <Lab />,
              },
            ],
          },
          {
            path: 'prescriptions',
            children: [
              {
                path: '',
                element: <Prescriptions />,
              },
              {
                path: ':id',
                element: <Prescription />,
              },
            ],
          },
          {
            path: 'assessments',
            children: [
              {
                path: '',
                element: (
                  <RoleGuard redirect allowed={[UserRole.Admin, UserRole.Provider]}>
                    <Assessments />
                  </RoleGuard>
                ),
              },
            ],
          },
          {
            path: 'lab-requests',
            children: [
              {
                path: '',
                element: (
                  <RoleGuard redirect allowed={[UserRole.Admin, UserRole.Provider]}>
                    <LabRequests />
                  </RoleGuard>
                ),
              },
            ],
          },
          {
            path: 'patients',
            children: [
              {
                path: '',
                element: <Patients />,
              },
              {
                path: ':id',
                children: [
                  {
                    path: '',
                    element: <Patient />,
                    children: [
                      {
                        path: '',
                        element: <ProfileTab />,
                      },
                      {
                        path: 'details',
                        element: <DetailsTab />,
                      },
                      {
                        path: 'health-history',
                        element: <HealthHistoryTab />,
                      },
                      {
                        path: 'conversations',
                        element: <ConversationsTab />,
                      },
                      {
                        path: 'documents',
                        element: <DocumentsTab />,
                      },
                      {
                        path: 'appointments',
                        element: <AppointmentsTab />,
                      },
                      {
                        path: 'Activity',
                        element: <ActivityTab />,
                      },
                      {
                        path: 'payments',
                        element: <PaymentsTab />,
                      },
                      {
                        path: 'quarterly_automations',
                        element: <QuarterlyAutomationsTab />,
                      },
                      {
                        path: 'Chat',
                        element: <ChatTab />,
                      },
                    ],
                  },
                  {
                    path: 'encounters/former/:encounterId',
                    element: <EncounterFormer />,
                  },
                  {
                    path: 'encounters/:encounterId',
                    children: [
                      {
                        path: '',
                        element: <Encounter />,
                      },
                      {
                        path: 'appointments/schedule',
                        element: (
                          <RoleGuard redirect allowed={[UserRole.Admin, UserRole.Provider, UserRole.CBO, UserRole.MA, UserRole.Support]}>
                            <PatientEncounterScheduleAppointment />
                          </RoleGuard>
                        ),
                      },
                      {
                        path: 'create-referral',
                        element: (
                          <RoleGuard redirect allowed={[UserRole.Provider]}>
                            <PatientEncounterCreateReferral />
                          </RoleGuard>
                        ),
                      },
                      {
                        path: 'recommend-vaccines',
                        element: (
                          <RoleGuard redirect allowed={[UserRole.Provider]}>
                            <PatientEncounterRecommendVaccine />
                          </RoleGuard>
                        ),
                      },
                      {
                        path: 'order-labs',
                        element: (
                          <RoleGuard redirect allowed={[UserRole.Admin, UserRole.Provider, UserRole.MA]}>
                            <PatientEncounterOrderLabs />
                          </RoleGuard>
                        ),
                      },
                      {
                        path: 'order-rx',
                        element: (
                          <RoleGuard redirect allowed={[UserRole.Provider]}>
                            <PatientEncounterOrderRx />
                          </RoleGuard>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'new',
                element: (
                  <RoleGuard redirect allowed={[UserRole.Admin, UserRole.CBO, UserRole.Support]}>
                    <PatientCreate />
                  </RoleGuard>
                ),
              },
            ],
          },
          {
            path: 'cbos',
            children: [
              {
                path: '',
                element: (
                  <RoleGuard redirect allowed={[UserRole.Admin]}>
                    <CBOs />
                  </RoleGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleGuard redirect allowed={[UserRole.Admin]}>
                    <CBOCreate />
                  </RoleGuard>
                ),
              },
              {
                path: ':id',
                children: [
                  {
                    path: '',
                    element: <CBO />,
                  },
                  {
                    path: ':tab',
                    element: <CBO />,
                  },
                  {
                    path: 'rule-set',
                    children: [
                      {
                        path: '',
                        element: <CBORuleSetDetails />,
                      },
                      {
                        path: 'edit',
                        element: <CBORuleSetEdit />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'pharmacies',
            children: [
              {
                path: '',
                element: (
                  <RoleGuard
                    redirect
                    allowed={[
                      UserRole.Admin,
                      UserRole.ClinicalAdmin,
                      UserRole.ClinicalLHC,
                      UserRole.ClinicalQI,
                      UserRole.MA,
                      UserRole.Provider,
                      UserRole.Support,
                      UserRole.Manager,
                      UserRole.Manager340B,
                      UserRole.Compliance,
                    ]}
                  >
                    <Pharmacies />
                  </RoleGuard>
                ),
              },
            ],
          },
          {
            path: 'users',
            children: [
              {
                path: '',
                element: (
                  <RoleGuard redirect allowed={[UserRole.Admin, UserRole.MA]}>
                    <Users />
                  </RoleGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleGuard redirect allowed={[UserRole.Admin]}>
                    <UserUpdate />
                  </RoleGuard>
                ),
              },
              {
                path: ':id',
                children: [
                  {
                    path: '',
                    children: [
                      {
                        path: '',
                        element: <User />,
                      },
                      {
                        path: ':tab',
                        element: <User />,
                      },
                    ],
                  },
                  {
                    path: 'edit',
                    element: (
                      <RoleGuard redirect allowed={[UserRole.Admin]}>
                        <UserUpdate />
                      </RoleGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'scheduling',
            element: (
              <RoleGuard
                redirect
                allowed={[
                  UserRole.Admin,
                  UserRole.ClinicalAdmin,
                  UserRole.ClinicalLHC,
                  UserRole.ClinicalQI,
                  UserRole.Support,
                  UserRole.MA,
                  UserRole.Manager,
                  UserRole.Manager340B,
                  UserRole.Provider,
                ]}
              >
                <Scheduling />
              </RoleGuard>
            ),
          },
          {
            path: 'reports',
            element: (
              <RoleGuard redirect prohibited={[UserRole.Compliance]}>
                <Reports />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/licenses',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <LicensesReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/enrolled-vs-prescriptions',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <EnrolledActivePatientsReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/enrolled-active-patients',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <EnrolledPrescriptionsReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/weekly-kpis',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <WeeklyKPIsReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/appointments-missed-and-rescheduled',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <AppointmentsMissedAndRescheduledReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/days-to-complete',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <DaysToCompleteReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/initials-completed',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <InitialsCompletedReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/enrollments-and-service-lines-scheduled',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <EnrollmentsAndServiceLinesScheduledReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/prep-initial-scheduled-from-daily-enrollment',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <PrepInitialScheduledFromDailyEnrollmentReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/new-vs-transfer-prep-patients',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <NewVsTransferPrepPatientsReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/operations/no-show-percent',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <NoShowPercentReport />
              </RoleGuard>
            ),
          },
          {
            path: 'reports/patient-journeys',
            element: (
              <RoleGuard redirect predicate={(me) => me.superAdmin}>
                <PatientJourneysReport />
              </RoleGuard>
            ),
          },
          {
            path: 'admin/platform-settings',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <PlatformSettings />
              </RoleGuard>
            ),
          },
          {
            path: 'notifications',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin, UserRole.Support]}>
                <NotificationLogs />
              </RoleGuard>
            ),
          },
          {
            path: 'report-logs',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin]}>
                <ReportLogs />
              </RoleGuard>
            ),
          },
          {
            path: 'doh-fax-numbers',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin, UserRole.Support, UserRole.MA]}>
                <DohFaxNumbers />
              </RoleGuard>
            ),
          },
          {
            path: 'visits/:id',
            element: (
              <RoleGuard redirect allowed={[UserRole.Provider]}>
                <VisitV2 />
              </RoleGuard>
            ),
          },
          {
            path: 'login-as',
            element: (
              <RoleGuard redirect allowed={[UserRole.Admin, UserRole.Support]}>
                <LoginAs />
              </RoleGuard>
            ),
          },
          /**
           * Separate page for handling internal app links.
           * This is used instead of direct links
           * because URLs can change and links will not be valid anymore.
           * Also, this allows us to handle links with additional logic.
           */
          {
            path: 'redirects/:value',
            element: <Redirects />,
          },
          {
            path: 'redirects/notification',
            element: <NotificationRedirect />,
          },
        ],
      },
      {
        path: '/call',
        element: (
          <AuthGuard>
            <Outlet />
          </AuthGuard>
        ),
        children: [
          {
            path: 'appointment/:id',
            element: <StandaloneAppointmentCall />,
          },
          {
            path: 'patient/:id',
            element: <StandalonePatientCall />,
          },
          {
            path: 'user/:id',
            element: <StandaloneUserCall />,
          },
        ],
      },
      {
        path: '/environment',
        element: <Environment />,
      },
      {
        path: '/message-preview',
        element: <MessagePreview />,
      },
      /**
       * Acts as a deep link entrypoint.
       * When a user follows a deep link and requires authentication,
       * once auth is completed, they are sent back here.
       * The url will include a `to` query param with the deep link.
       */
      {
        path: '/redirect',
        element: <Redirect />,
      },
      {
        path: '*',
        children: [
          {
            path: '401',
            element: <NotAuthorized />,
          },
          {
            path: '404',
            element: <NotFound />,
          },
          {
            path: '500',
            element: <Error />,
          },
          {
            path: '403',
            element: <Forbidden />,
          },
          {
            path: '*',
            element: <NotFound />,
          },
        ],
      },
    ],
  },
])

export default router
